import { useState, useCallback, useEffect } from "react";
import { MESSAGE_MEDIA_TYPES } from "../constants/messageConstants";

/**
 * 🎬 useMediaState - Hook para gerenciar estados de mídia
 * 
 * Fornece controle avançado de estado para diferentes tipos de mídia,
 * incluindo carregamento, erro, reprodução e interações do usuário.
 * 
 * @param {Object} message - Objeto da mensagem com dados de mídia
 * @returns {Object} - Estados e funções para controle de mídia
 */
export const useMediaState = (message) => {
  // 🏗️ Estados básicos
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  // 🎯 Verificar se a mídia é válida
  const isValidMedia = useCallback(() => {
    // Permitir locationMessage e contactMessage mesmo sem mediaUrl (dados ficam no body)
    return message?.mediaType && (
      message?.mediaUrl || 
      message.mediaType === "locationMessage" || 
      message.mediaType === "contactMessage"
    );
  }, [message]);

  // 🎯 Normaliza o tipo de mídia para os valores esperados pelo renderer
  const normalizeMediaType = useCallback(() => {
    if (!message?.mediaType) return null;

    const rawType = String(message.mediaType).toLowerCase();

  if (["image", "imagemessage", "sticker", "stickermessage"].includes(rawType)) {
      return MESSAGE_MEDIA_TYPES.IMAGE;
    }

    if (["video", "videomessage"].includes(rawType)) {
      return MESSAGE_MEDIA_TYPES.VIDEO;
    }

    if (["audio", "audiomessage"].includes(rawType)) {
      return MESSAGE_MEDIA_TYPES.AUDIO;
    }

    if ([
      "application",
      "document",
      "documentmessage",
      "documentwithcaptionmessage",
      "text"
    ].includes(rawType)) {
      return MESSAGE_MEDIA_TYPES.APPLICATION;
    }

    if (["locationmessage", "location"].includes(rawType)) {
      return MESSAGE_MEDIA_TYPES.LOCATION;
    }

    if (["contactmessage", "contactsarraymessage", "contact"].includes(rawType)) {
      return MESSAGE_MEDIA_TYPES.CONTACT;
    }

    return message.mediaType;
  }, [message?.mediaType]);

  // 🎯 Verificar tipo de mídia
  const getMediaType = useCallback(() => {
    if (!isValidMedia()) return null;
    return normalizeMediaType();
  }, [isValidMedia, normalizeMediaType]);

  // 🎯 Verificar se é mídia visual (imagem/vídeo)
  const isVisualMedia = useCallback(() => {
    const mediaType = getMediaType();
    return mediaType === MESSAGE_MEDIA_TYPES.IMAGE || 
           mediaType === MESSAGE_MEDIA_TYPES.VIDEO;
  }, [getMediaType]);

  // 🎯 Verificar se é mídia de áudio
  const isAudioMedia = useCallback(() => {
    return getMediaType() === MESSAGE_MEDIA_TYPES.AUDIO;
  }, [getMediaType]);

  // 🎯 Verificar se é arquivo para download
  const isDownloadableFile = useCallback(() => {
    return getMediaType() === MESSAGE_MEDIA_TYPES.APPLICATION;
  }, [getMediaType]);

  // 🎯 Verificar se é mídia interativa (localização/contato)
  const isInteractiveMedia = useCallback(() => {
    const mediaType = getMediaType();
    return mediaType === MESSAGE_MEDIA_TYPES.LOCATION || 
           mediaType === MESSAGE_MEDIA_TYPES.CONTACT;
  }, [getMediaType]);

  // 🎯 Handlers de estado
  const handleLoadStart = useCallback(() => {
    setIsLoading(true);
    setHasError(false);
  }, []);

  const handleLoadEnd = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback((error) => {
    setIsLoading(false);
    setHasError(true);
    console.error('Erro ao carregar mídia:', error);
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
  }, []);

  const handleProgress = useCallback((currentTime, totalTime) => {
    setProgress(currentTime);
    setDuration(totalTime);
  }, []);

  const handleVolumeChange = useCallback((newVolume) => {
    setVolume(Math.max(0, Math.min(1, newVolume)));
  }, []);

  const handleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // 🎯 Funções de controle
  const resetState = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setIsFullscreen(false);
  }, []);

  const downloadMedia = useCallback(() => {
    if (!isValidMedia()) return;
    
    const link = document.createElement('a');
    link.href = message.mediaUrl;
    link.download = message.filename || message.originalName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [message, isValidMedia]);

  const shareMedia = useCallback(async () => {
    if (!isValidMedia() || !navigator.share) return;
    
    try {
      await navigator.share({
        title: 'Mídia compartilhada',
        url: message.mediaUrl,
      });
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
    }
  }, [message, isValidMedia]);

  const copyMediaUrl = useCallback(async () => {
    if (!isValidMedia()) return;
    
    try {
      await navigator.clipboard.writeText(message.mediaUrl);
      return true;
    } catch (error) {
      console.error('Erro ao copiar URL:', error);
      return false;
    }
  }, [message, isValidMedia]);

  // 🎯 Formatadores de dados
  const formatDuration = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const formatFileSize = useCallback((bytes) => {
    if (!bytes || isNaN(bytes)) return 'Tamanho desconhecido';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  }, []);

  const getFileExtension = useCallback((filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  }, []);

  // 🎯 Metadados da mídia
  const mediaMetadata = {
    type: getMediaType(),
    isValid: isValidMedia(),
    isLoading,
    hasError,
    isPlaying,
    isFullscreen,
    progress,
    duration,
    volume,
    formattedDuration: formatDuration(duration),
    formattedProgress: formatDuration(progress),
    fileName: message?.filename || message?.originalName || 'Arquivo',
    fileSize: message?.fileSize ? formatFileSize(message.fileSize) : null,
    fileExtension: getFileExtension(message?.filename || message?.originalName || ''),
    isVisual: isVisualMedia(),
    isAudio: isAudioMedia(),
    isDownloadable: isDownloadableFile(),
    isInteractive: isInteractiveMedia(),
  };

  // 🎯 Ações disponíveis
  const mediaActions = {
    onLoadStart: handleLoadStart,
    onLoadEnd: handleLoadEnd,
    onError: handleError,
    onPlay: handlePlay,
    onPause: handlePause,
    onStop: handleStop,
    onProgress: handleProgress,
    onVolumeChange: handleVolumeChange,
    onFullscreen: handleFullscreen,
    reset: resetState,
    download: downloadMedia,
    share: shareMedia,
    copyUrl: copyMediaUrl,
  };

  // 🎯 Cleanup ao desmontar
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return {
    ...mediaMetadata,
    actions: mediaActions,
  };
};

export default useMediaState;
